// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.D3LegendDrawing.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

// Legend padding
const LEGEND_PADDING = 8;
// Legend symbol padding
const LEGEND_SYMBOL_PADDING = 5;
// Legend default background
const LEGEND_BACKGROUND = '#FFF';
// Margin inside legend box
const LEGEND_BOX_MARGIN = 11;
// Outer margin of legend to chart border
const LEGEND_OUTER_MARGIN = 17;

import { isArray, isNil, isEmpty, toNumber } from 'lodash/fp';
import { d3 } from '../Izenda.D3Wrapper';
import { DISABLE_LEGEND_COLOR, LAYOUT_CLASSES } from 'logic/data/Izenda.D3VizEngineConstants';
import { addClipPath } from '../Izenda.D3DrawUtils';

/**
 * Draw the chart legend
 * @param {d3.Selection<SVGElement>} svg
 * @param {Object} options
 * @param {Number} chartWidth
 * @param {Number} chartHeight
 * @param {Object} margin
 * @param {Function} drawFunction
 */
export function drawLegend(
  svg: d3.Selection<SVGElement>,
  options: Object,
  chartWidth: Number,
  chartHeight: Number,
  margin: Object
) {
  const { legend, series } = options;

  if (legend.enabled) {
    const { itemStyle, verticalAlign, align, borderVisible } = legend;
    const legendGraphic = svg
      .append('g')
      .classed(LAYOUT_CLASSES.legend.class, true)
      .datum(LAYOUT_CLASSES.legend);
    const legendBox = legendGraphic
      .append('rect')
      .classed('d3-legend-box', true)
      .attr('fill', legend.backgroundColor || LEGEND_BACKGROUND);

    const contentGraphic = legendGraphic.append('g').classed('d3-legend-content', true);

    const items = isArray(series) ? series : [series];

    items.map((item, index) => {
      const { display = true } = item;
      const itemElement = contentGraphic
        .append('g')
        .classed('d3-legend-item', true)
        .attr('transform', `translate(${LEGEND_PADDING}, ${LEGEND_PADDING})`);
      const fontSize = parseFloat(itemStyle.fontSize);

      const serieSymbol = itemElement
        .append('rect')
        .attr('width', fontSize)
        .attr('height', fontSize)
        .attr('rx', fontSize / 2)
        .attr('ry', fontSize / 2)
        .attr('fill', display ? item.color : DISABLE_LEGEND_COLOR);

      const { width: symbolWidth, height: symbolHeight } = serieSymbol.node().getBBox();

      const serieText = itemElement
        .append('text')
        .attr('x', symbolWidth + LEGEND_SYMBOL_PADDING)
        .style('font-family', itemStyle.fontFamily)
        .style('font-size', itemStyle.fontSize)
        .style('font-weight', itemStyle.fontWeight)
        .style('font-style', itemStyle.fontStyle)
        .style('text-decoration', itemStyle.textDecoration)
        .style('background-color', itemStyle.backgroundColor)
        .style('fill', display ? itemStyle.color : DISABLE_LEGEND_COLOR)
        .attr('text-anchor', 'start');

      serieText.append('tspan').text(item.name);
      serieText.append('title').text(item.name);

      const { height: textHeight } = serieText.node().getBBox();
      serieText.attr('y', Math.floor(symbolHeight - (textHeight - symbolHeight) / 2));

      itemElement.on('click.legend', () =>
        toggleSeriesVisibility(svg, options, index, serieSymbol, serieText, item, itemStyle)
      );
      return itemElement;
    });

    let { height: legendHeight, width: legendWidth } = contentGraphic.node().getBBox();

    legendWidth = Math.floor(legendWidth + LEGEND_PADDING * 2);
    legendHeight = Math.floor(legendHeight + LEGEND_BOX_MARGIN);

    legendBox.attr('width', legendWidth).attr('height', legendHeight);

    const {
      remainingWidth,
      remainingHeight,
      legendTopTranslate,
      legendLeftTranslate,
      topTranslate,
      leftTranslate
    } = layoutLegend(svg, legendGraphic, verticalAlign, align, chartWidth, chartHeight, margin);

    legendGraphic.attr('transform', `translate(${Math.floor(legendLeftTranslate)}, ${Math.floor(legendTopTranslate)})`);

    if (borderVisible) {
      const { borderColor, borderWidth = 0 } = legend;
      legendBox.attr('stroke', borderColor).attr('stroke-width', borderWidth);
      crispBorderEdges(legendBox, borderWidth);
    }

    return { remainingWidth, remainingHeight, topTranslate, leftTranslate };
  }

  return { remainingWidth: chartWidth, remainingHeight: chartHeight, topTranslate: 0, leftTranslate: 0 };
}

/**
 * Toggle visibility of series group when clicking legend
 * @param {d3.Selection<SVGSVGElement>} svg
 * @param {Object} options
 * @param {Number} index
 * @param {d3.Selection<SVGElement>} serieSymbol
 * @param {d3.Selection<SVGTextElement>} serieText
 * @param {Object} item
 * @param {Object} itemStyle
 */
function toggleSeriesVisibility(
  svg: d3.Selection<SVGSVGElement>,
  options: Object,
  index: Number,
  serieSymbol: d3.Selection<SVGElement>,
  serieText: d3.Selection<SVGTextElement>,
  item: Object,
  itemStyle: Object
) {
  options.series[index].display = !(isNil(options.series[index].display) || options.series[index].display);

  const seriesGraphic = svg.select(`g.${LAYOUT_CLASSES.seriesGroup.class} > .d3-series-${index}`);
  const labelGraphic = svg.select(`g.${LAYOUT_CLASSES.labelGroup.class} > .d3-series-${index}`);
  const isVisible = options.series[index].display;

  // Set visibility for the graphic
  seriesGraphic.style('visibility', isVisible ? 'visible' : 'hidden');
  labelGraphic.style('visibility', isVisible ? 'visible' : 'hidden');

  // Set color for the legend
  serieSymbol.style('fill', isVisible ? item.color : DISABLE_LEGEND_COLOR);
  serieText.style('fill', isVisible ? itemStyle.color : DISABLE_LEGEND_COLOR);

  // Set visibility for all text in this series
  labelGraphic.selectAll('text').each(function() {
    this.style.visibility = isVisible ? '' : 'hidden';
  });
}

/**
 * Layout the legend
 * @param {d3.Selection<SVGGElement>} legendGraphic
 * @param {String} verticalAlign
 * @param {String} horizontalAlign
 * @param {Number} chartWidth
 * @param {Number} chartHeight
 * @param {Object} margin
 */
function layoutLegend(
  svg: d3.Selection<SVGElement>,
  legendGraphic: d3.Selection<SVGGElement>,
  verticalAlign: String,
  horizontalAlign: String,
  chartWidth: Number,
  chartHeight: Number,
  margin: Object
) {
  let legendLeftTranslate = 0;
  let legendTopTranslate = 0;
  let topTranslate = 0;
  let leftTranslate = 0;
  let remainingHeight = chartHeight;
  let remainingWidth = chartWidth;
  let maxWidth = 0;

  const { height: legendHeight, width: legendWidth } = legendGraphic.node().getBBox();

  const maxHeight = Math.min((chartHeight - (margin.top + margin.bottom)) / 2, legendHeight);
  const legendBox = legendGraphic.select('.d3-legend-box');

  if (verticalAlign === 'top') {
    maxWidth = chartWidth - (margin.left + margin.right);
    if (legendWidth > maxWidth) {
      legendBox.attr('width', maxWidth);
      legendGraphic.selectAll('tspan').each(wrapText(maxWidth - LEGEND_PADDING * 2));
    } else {
      maxWidth = legendWidth;
    }

    if (horizontalAlign === 'left') {
      legendLeftTranslate = margin.left;
    }
    if (horizontalAlign === 'center') {
      legendLeftTranslate = (chartWidth - maxWidth) / 2;
    }
    if (horizontalAlign === 'right') {
      legendLeftTranslate = chartWidth - (maxWidth + margin.right);
    }

    legendTopTranslate = margin.top + LEGEND_OUTER_MARGIN;
    topTranslate = legendTopTranslate + maxHeight;
    remainingHeight -= topTranslate;
  }

  if (verticalAlign === 'middle') {
    maxWidth =
      horizontalAlign === 'center'
        ? chartWidth - (margin.left + margin.right)
        : (chartWidth - (margin.left + margin.right)) / 2;
    if (legendWidth > maxWidth) {
      legendBox.attr('width', maxWidth);
      legendGraphic.selectAll('tspan').each(wrapText(maxWidth - LEGEND_PADDING * 2));
    } else {
      maxWidth = legendWidth;
    }

    if (horizontalAlign === 'left') {
      legendLeftTranslate = margin.left;
      remainingWidth -= maxWidth + LEGEND_OUTER_MARGIN;
      leftTranslate = maxWidth + LEGEND_OUTER_MARGIN;
    }
    if (horizontalAlign === 'center') {
      legendLeftTranslate = (chartWidth - maxWidth) / 2;
    }
    if (horizontalAlign === 'right') {
      legendLeftTranslate = chartWidth - (maxWidth + margin.right);
      remainingWidth -= maxWidth + LEGEND_OUTER_MARGIN;
    }

    legendTopTranslate = (chartHeight - maxHeight - margin.top) / 2;
  }

  if (verticalAlign === 'bottom') {
    maxWidth = chartWidth - (margin.left + margin.right);
    if (legendWidth > maxWidth) {
      legendBox.attr('width', maxWidth);
      legendGraphic.selectAll('tspan').each(wrapText(maxWidth - LEGEND_PADDING * 2));
    } else {
      maxWidth = legendWidth;
    }

    if (horizontalAlign === 'left') {
      legendLeftTranslate = margin.left;
    }
    if (horizontalAlign === 'center') {
      legendLeftTranslate = (chartWidth - maxWidth) / 2;
    }
    if (horizontalAlign === 'right') {
      legendLeftTranslate = chartWidth - (maxWidth + margin.right);
    }

    legendTopTranslate = chartHeight - (margin.bottom + maxHeight);
    remainingHeight -= margin.bottom + maxHeight;
  }

  const { id } = addClipPath(svg, maxWidth, maxHeight);
  legendGraphic.attr('clip-path', `url(#${id})`);

  return { remainingHeight, remainingWidth, legendTopTranslate, legendLeftTranslate, topTranslate, leftTranslate };
}

/**
 * Wrap text with ellipsis until smaller than width
 * @param {Number} width
 */
function wrapText(width: Number) {
  return function() {
    const tspan = d3.select(this);
    const textNode = d3.select(tspan.node().parentNode);
    const textX = toNumber(textNode.attr('x'));
    let textLength = tspan.node().getComputedTextLength();
    let currentText = tspan.text();

    while (textX + textLength > width - LEGEND_PADDING && !isEmpty(currentText)) {
      currentText = currentText.slice(0, -1);
      tspan.text(`${currentText}…`);
      textLength = tspan.node().getComputedTextLength();
    }
  };
}

/**
 * Crisp the border edges
 * @param {d3.Selection<SVGGElement>} legendBox
 * @param {Number} borderWidth
 */
function crispBorderEdges(legendBox: d3.Selection<SVGGElement>, borderWidth: Number) {
  const normalizer = (borderWidth % 2) / 2;

  legendBox.attr('x', normalizer);
  legendBox.attr('y', normalizer);

  legendBox.attr('width', Math.floor(legendBox.attr('width') - 2 * normalizer));
  legendBox.attr('height', Math.floor(legendBox.attr('height') - 2 * normalizer));
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/commonDrawing/Izenda.D3LegendDrawing.js